import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import type { ReportExportRequest, ReportQueryKeyEntries } from '@/lib/services/reports/reports.api'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import {
  exportPlatformLedgerEntries,
  getPlatformLedgerEntries,
  getPlatformLedgerSummary,
  type PlatformLedgerSummaryFilters,
} from './platform-ledger.api'
import type { PlatformLedgerReportFilters } from './platform-ledger.schema'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface PlatformLedgerScope<TFilters> extends OrganizationScope {
  filters?: TFilters | undefined
}

/** The key entries each platform ledger fetcher reads; the scoped key builders below set them. */
type PlatformLedgerQueryKey<TFilters> = [AdminQueryRoot & ReportQueryKeyEntries<TFilters>]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type PlatformLedgerQueryParams<TOptions> = OrganizationScope &
  Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

function platformLedgerQueryKey<TFilters>(
  scope: string,
  { organizationId, filters }: PlatformLedgerScope<TFilters>
): PlatformLedgerQueryKey<TFilters> {
  return buildScopedAdminQueryKey(platformLedgerQueryKeys.all({ organizationId })[0], {
    organizationId,
    scope,
    filters,
  })
}

export const platformLedgerQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'platform-ledger' }, { organizationId }),
  ],
  summary: (scope: PlatformLedgerScope<PlatformLedgerSummaryFilters> = {}) =>
    platformLedgerQueryKey('summary', scope),
  entries: (scope: PlatformLedgerScope<PlatformLedgerReportFilters> = {}) =>
    platformLedgerQueryKey('entries', scope),
}

export const platformLedgerQueries = {
  summary: ({ organizationId, filters }: PlatformLedgerScope<PlatformLedgerSummaryFilters> = {}) =>
    queryOptions({
      queryKey: platformLedgerQueryKeys.summary({ organizationId, filters }),
      queryFn: getPlatformLedgerSummary,
    }),
  entries: ({ organizationId, filters }: PlatformLedgerScope<PlatformLedgerReportFilters> = {}) =>
    queryOptions({
      queryKey: platformLedgerQueryKeys.entries({ organizationId, filters }),
      queryFn: getPlatformLedgerEntries,
    }),
}

export function usePlatformLedgerSummary(
  filters?: PlatformLedgerSummaryFilters,
  {
    organizationId,
    ...params
  }: PlatformLedgerQueryParams<ReturnType<typeof platformLedgerQueries.summary>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...platformLedgerQueries.summary({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function usePlatformLedgerEntries(
  filters?: PlatformLedgerReportFilters,
  {
    organizationId,
    ...params
  }: PlatformLedgerQueryParams<ReturnType<typeof platformLedgerQueries.entries>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...platformLedgerQueries.entries({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useExportPlatformLedgerEntriesMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: ReportExportRequest<PlatformLedgerReportFilters>) =>
      exportPlatformLedgerEntries({ organizationId, filters }),
  })
}
